<template>
  <div v-if="matter && isVisible">
    <table class="table table-sm mt-3 matter-realization-scopes">
      <thead>
        <tr>
          <th>
            <div class="d-flex">
              <div class="mx-auto align-self-center">
                {{ entityNamePlural.toUpperCase() }}
              </div>
              <ButtonSmall
                v-if="canEdit"
                :tooltip_text="
                  $t('reusable.common.editTooltip', {
                    entity: entityNamePlural,
                  })
                "
                :click_button="() => (show_selection = true)"
                :class="`mx-1`"
                :icon="'square'"
                :custom_class="'text-white'"
              ></ButtonSmall>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="pt-2">
            <template v-if="matterScopes.length > 0">
              <p
                v-for="realization_scope in matterScopes"
                :key="realization_scope.id"
                class="text-left pl-4 mb-1"
              >
                {{ realization_scope.name }}
              </p>
            </template>
            <div v-else>
              {{
                $t("matterProgram.matterProgramView.noAssociatedEntityToShow", {
                  entity: entityName,
                })
              }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <BsModal
      v-model="show_selection"
      :title="`${$t('reusable.common.selectLabel')} ${entityNamePlural}`"
      size="lg"
    >
      <MatterRealizationScopeSelection
        v-if="show_selection"
        :egress_profile_matter="matter"
        :competence_ids="matter.competences"
        :matrix2_competences="matrix2_competences"
        @close="show_selection = false"
      ></MatterRealizationScopeSelection>
    </BsModal>
  </div>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Ámbitos de realización de la asignatura en su programa (ver $usesRealizationScopes):
// equivalente a MatterCapacities, pero con el listado propio del perfil de egreso.
// Legacy (Bootstrap-Vue): ButtonSmall es el botón compartido de la app.
import { computed, getCurrentInstance, ref, watch } from "vue";
import * as names from "@/store/names";
import {
  scopeIdsOfCompetences,
  scopesOfEgressProfile,
  selectedScopesOfMatter,
} from "@/utils/realizationScopes";
import BsModal from "@/components/reusable/bs/BsModal";
import ButtonSmall from "@/components/reusable/Buttons/Small/ButtonSmall";
import MatterRealizationScopeSelection from "@/components/mesh/EgressProfile/RealizationScopes/MatterRealizationScopeSelection";

const props = defineProps({
  // Competencias del perfil con sus realization_scopes (id = id de la competencia)
  matrix2_competences: {
    type: Array,
    default: () => [],
  },
  egress_profile_matter_id: {
    type: Number,
    required: true,
  },
  allows_crud: {
    type: Boolean,
    default: false,
  },
  can_update: {
    type: Boolean,
    default: false,
  },
});

const proxy = getCurrentInstance().proxy;
const store = proxy.$store;

const entityName = computed(() =>
  proxy.$getVisibleNames(
    "mesh.egressprofilerealizationscope",
    false,
    "Ámbito de realización"
  )
);
const entityNamePlural = computed(() =>
  proxy.$getVisibleNames(
    "mesh.egressprofilerealizationscope",
    true,
    "Ámbitos de realización"
  )
);

const matter = computed(() =>
  store.getters[names.EGRESS_PROFILES_MATTERS].find(
    (x) => x.id == props.egress_profile_matter_id
  )
);
const canEdit = computed(() => props.allows_crud && props.can_update);

const profileScopes = computed(() =>
  matter.value
    ? scopesOfEgressProfile(
        store.getters[names.REALIZATION_SCOPES],
        matter.value.egress_profile
      )
    : []
);

const matterScopes = computed(() => {
  if (!matter.value) return [];
  return selectedScopesOfMatter(
    profileScopes.value,
    scopeIdsOfCompetences(props.matrix2_competences, matter.value.competences),
    matter.value.realization_scopes || []
  );
});

// Sin modo edición solo se muestra si la asignatura tiene ámbitos; en modo edición, si el
// perfil tiene ámbitos que elegir.
const isVisible = computed(
  () =>
    matterScopes.value.length > 0 ||
    (canEdit.value && profileScopes.value.length > 0)
);

const show_selection = ref(false);

watch(
  () => matter.value && matter.value.egress_profile,
  (egress_profile_id) => {
    if (egress_profile_id)
      store.dispatch(names.FETCH_REALIZATION_SCOPES, {
        egress_profile_id: egress_profile_id,
      });
  },
  { immediate: true }
);
</script>

<style scoped>
td {
  border: 1px solid var(--kl-table-border) !important;
  text-align: center;
  vertical-align: middle;
  font-size: var(--secondary-font-size);
  padding: 2px 2px;
}
th {
  vertical-align: middle;
  border: 1px solid var(--kl-table-border) !important;
  color: var(--kl-text-on-primary) !important;
  background-color: var(--kl-color-accent-medium) !important;
  font-size: var(--primary-font-size);
  padding: 2px 2px;
}
</style>
